import React from 'react';
import FormControlledInputArea from '../../../../components/inputs/FormInputs/FormControlledInputArea';
import WrapperButton from '../../../../components/buttons/WrapperButton';
import FormControlledCalendar from '../../../../components/inputs/FormInputs/FormControlledCalendar';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { ClaimForm } from '@/src/app/Interfaces/Insurance/Claim';
import SingleFileInput from '@/src/app/components/inputs/StyledInputs/SingleFileInput';
import { transformBToMB } from '@/src/utils/fileUtil';

type ClaimLogFormProps = {
  fieldIndex: number;
  remove: (index: number) => void;
  fileLimit: number;
  changeLimit: (amount: number) => void;
};

const ClaimLogForm = ({
  fieldIndex,
  remove,
  fileLimit,
  changeLimit,
}: ClaimLogFormProps) => {
  const { control, setValue, getValues } = useFormContext<ClaimForm>();
  const [date, description, removed, prevAttachment] = useWatch<ClaimForm>({
    name: [
      `logs.${fieldIndex}.date`,
      `logs.${fieldIndex}.description`,
      `logs.${fieldIndex}.removeAttachment`,
      `logs.${fieldIndex}.prevAttachment`,
    ],
  });

  return (
    <React.Fragment>
      <div className="col-12 pb-0 flex gap-4 align-content-center">
        <label className="text-17px block font-bold">
          Log {fieldIndex + 1}
        </label>
        <WrapperButton
          onClick={() => {
            const attachment = getValues(`logs.${fieldIndex}.attachment`);
            if (attachment) {
              changeLimit(attachment.size);
            }
            
            remove(fieldIndex);
          }}
          type="button"
        >
          <i className="pi pi-trash" />
        </WrapperButton>
      </div>
      <div className="col-12 sm:col-6 mr-auto">
        <label
          htmlFor={`logs.${fieldIndex}.date`}
          className="text-standard block mb-2"
        >
          Date
        </label>
        <FormControlledCalendar
          formID={`logs.${fieldIndex}.date`}
          readOnlyInput
          showButtonBar
          className="w-full"
          isFieldArray={true}
          rules={{
            validate: (value: Date) => {
              if (value) {
                return true;
              }

              return description ? 'Log Date is required' : true;
            },
          }}
        />
      </div>
      <div className="col-12 pt-0">
        <label
          htmlFor={`logs.${fieldIndex}.description`}
          className="text-standard block mb-2"
        >
          Description
        </label>
        <FormControlledInputArea
          defaultValue=""
          formID={`logs.${fieldIndex}.description`}
          rows={3}
          cols={60}
          isFieldArray={true}
          rules={{
            validate: (value: string) => {
              if (value) {
                return true;
              }

              return date ? 'Log Description is required' : true;
            },
          }}
        />
      </div>
      <div className="col-12 pt-0">
        <label htmlFor="attachment" className="text-standard block mb-2">
          Attachment
        </label>
        <div>
          {prevAttachment && !removed && (
            <div>
              <div className="flex">
                Current attachment:
                <div className="flex flex-column gap-2">
                  <span className="flex">
                    <span className="ml-1 mr-2">
                      {prevAttachment as string}
                    </span>
                    <i
                      onClick={() => {
                        setValue(`logs.${fieldIndex}.removeAttachment`, true);
                      }}
                      className={`pi pi-ban errorText cursor-pointer my-auto`}
                    />
                  </span>
                </div>
              </div>
              <div className="mt-2 mb-1">
                To add a new attachment select it below:
              </div>
            </div>
          )}
          <Controller
            name={`logs.${fieldIndex}.attachment`}
            aria-label="file-input"
            control={control}
            render={({ field: { onChange, value } }) => (
              <div>
                <SingleFileInput
                  value={value}
                  onChange={(event) => {
                    onChange(event);
                    if (event) {
                      changeLimit(event.size * -1);
                    } else {
                      changeLimit(value?.size ?? 0);
                    }
                  }}
                  acceptedFormats={['.pdf']}
                  totalLimit={fileLimit + (value?.size ?? 0)}
                  totalLimitLabel={`${transformBToMB(
                    fileLimit + (value?.size ?? 0)
                  )} MB`}
                  emptyMessage={`Upload attachment file. Allowed extensions: ${[
                    '.pdf',
                  ].join(', ')}.`}
                />
              </div>
            )}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default ClaimLogForm;
